import { Button } from '@/app/common/components/button/Button'
import { buttonVariants } from '@/app/common/components/button/buttonVariants'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { TrashCanIcon } from '@/app/common/icons/general'
import { parseRequestError } from '@/app/common/utilities'
import { cn } from '@/lib/utils'
import { useDisableProgramCatalogItem } from '@/queries/program_catalog/hooks/useDisableProgramCatalogItem'
import { ProgramItem } from '@/queries/program_catalog/schema'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/shad/ui/dialog'
import { useToast } from '@/shad/ui/toast/use-toast'
import { selectSelectedProgram } from '@/storage/programs/slice'
import React from 'react'
import { useSelector } from 'react-redux'

type Props = {
  item: ProgramItem
  onRemoveSuccess: () => void
}

export default function RemoveItemPopup(props: Props) {
  const { item, onRemoveSuccess } = props
  const [isOpen, setIsOpen] = React.useState(false)
  const { toast } = useToast()
  const disableItemMutation = useDisableProgramCatalogItem({
    onSuccess: () => {
      onRemoveSuccess()
      setIsOpen(false)
      toast({
        title: 'Item removed from program',
        description: `${item.global_item_name} has been removed from the program.`,
        variant: 'default',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error removing item',
        description: parseRequestError(error).message,
        variant: 'destructive',
      })
    },
  })
  const programId = useSelector(selectSelectedProgram).id

  const isDisabled = item.status === 'Inactive'
  return (
    <Dialog onOpenChange={setIsOpen} open={isOpen}>
      <DialogTrigger className="flex items-center gap-2" asChild>
        <button
          disabled={isDisabled}
          className={cn(
            'w-full hover:bg-gray-100 text-left transition rounded px-2  py-2',
            { 'cursor-not-allowed opacity-50': isDisabled }
          )}
        >
          <TrashCanIcon /> Remove from program
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>Remove Category from Program</DialogTitle>
        <p className="text-sm text-gray-700">
          Are you sure you want to remove {item.global_item_name} from this
          program? Doing so will disable all items for associated with this
          category for all applicants and providers within this program.
        </p>
        <div className="flex justify-end gap-2 mt-4">
          <div>
            <SubmitButton
              className={buttonVariants({
                variant: 'destructive',
              })}
              onClick={() => {
                disableItemMutation.mutate({
                  programId: programId,
                  itemId: item.id,
                })
              }}
              submittingText="Removing..."
              buttonTxt="Remove"
              status={disableItemMutation.status}
            ></SubmitButton>
          </div>
          <Button
            onClick={() => {
              setIsOpen(false)
            }}
            variant={'outline'}
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
